import SaveOutlined from "@mui/icons-material/SaveOutlined";
import { Button } from "@mui/material";
import { ReactNode, useRef } from "react";

export type SaveButtonProps = {
  /** When provided, enables a double-click guard that blocks repeated clicks
   * before React re-renders with the new loading state. */
  loading?: boolean;
  loadingText?: string;
  disabled?: boolean;
  endIcon?: ReactNode;
  /** When `loading` is provided, this callback should return its async work
   * so the double-click guard stays active until the promise settles. */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  saveText?: string;
  startIcon?: ReactNode;
  hideStartIcon?: boolean;
  formId?: string;
};

export const SaveButton: React.FC<SaveButtonProps> = ({
  loading: isLoading,
  loadingText = "Saving...",
  disabled: isDisabled,
  onClick,
  formId,
  saveText = "Save",
  startIcon = <SaveOutlined />,
  hideStartIcon,
  endIcon,
}) => {
  // Double-click guard: blocks repeated clicks before React re-renders with
  // isLoading=true. Only active when the caller passes isLoading —
  // callers without it aren't performing async mutations so double-click
  // protection isn't needed, and we'd have no signal to reset the ref.
  const hasLoadingProp = isLoading !== undefined;
  const inFlightRef = useRef(false);

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    if (hasLoadingProp && inFlightRef.current) {
      e.preventDefault();
      return;
    }
    inFlightRef.current = true;
    try {
      await onClick?.(e);
    } finally {
      inFlightRef.current = false;
    }
  };

  return (
    <Button
      type="submit"
      variant="contained"
      disabled={isDisabled || isLoading}
      startIcon={!hideStartIcon && !isLoading && startIcon}
      endIcon={endIcon && !isLoading ? endIcon : undefined}
      onClick={handleClick}
      form={formId}
    >
      {isLoading ? loadingText : saveText}
    </Button>
  );
};
